import React, { useMemo } from 'react';
import { Alert, Card, Descriptions, Empty, Progress, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type {
  ScenarioEconomyApplied,
  ScenarioEconomyRejected,
  ScenarioEconomyState,
} from '../../api/seasonsAdminApi';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../i18n';

const { Text } = Typography;

/**
 * Экономика ПРОХОЖДЕНИЯ в отладочной консоли
 * (RFC_SCENARIO_AUTHORING_ARCHITECTURE §4).
 *
 * Панель отвечает на два вопроса автора, на которые снимок мира не отвечает:
 *
 *  1. Хватило ли денег на решение, которое задумано как выбор? Стартовый баланс,
 *     выручка и траты показаны рядом, потому что «купить укрытие» имеет смысл
 *     только если продажа его окупает — и это видно числами, а не намерением.
 *  2. ПОЧЕМУ команда не сработала? Отказ движка — не исключение и не пропавшее
 *     действие: он приходит строкой с кодом. Без этого списка автор видел бы
 *     лишь «баланс не изменился» и искал бы ошибку в симуляции.
 */

/** Engine rejection codes that have an authoring hint in `economyPanel.rejection.<code>`. */
const REJECTION_CODES = new Set([
  'sell_nothing_in_storage',
  'sell_exceeds_storage',
  'sell_crop_not_priced',
  'buy_insufficient_funds',
  'buy_out_of_stock',
  'install_item_not_owned',
  'install_window_in_past',
  'install_window_too_long',
  'install_window_unbounded',
  'install_covering_conflict',
  'install_unknown_bed',
  'command_id_conflict',
  'command_unknown_type',
]);

const money = (value: number, currency: string) => `${value} ${currency}`;

const formatKg = (kg: number | undefined) => formatNumber(kg, { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const kgRows = (t: TFunction, map: Record<string, number>) => Object.entries(map)
  .filter(([, kg]) => kg > 0)
  .map(([crop, kg]) => t('scenarios_admin:economyPanel.cropKg', { crop, kg: formatKg(kg) }));

const describeApplied = (t: TFunction, row: ScenarioEconomyApplied): string => {
  switch (row.type) {
    case 'sell_harvest':
      return t('scenarios_admin:economyPanel.applied.sold', {
        crop: row.crop,
        kg: formatKg(row.quantityKg),
        price: row.unitPrice,
      });
    case 'buy_item':
      return t('scenarios_admin:economyPanel.applied.bought', { item: row.itemId, quantity: row.quantity });
    case 'install_covering':
      return t('scenarios_admin:economyPanel.applied.covered', {
        bed: row.bedId,
        from: row.activeFrom,
        to: row.activeTo || t('scenarios_admin:economyPanel.applied.noEnd'),
      });
    default:
      return row.type;
  }
};

export const ScenarioEconomyPanel: React.FC<{
  economy: ScenarioEconomyState | null;
  specErrors?: Array<{ code: string; path: string; message: string }>;
}> = ({ economy, specErrors = [] }) => {
  const { t } = useTranslation('scenarios_admin');
  const storage = useMemo(() => (economy ? kgRows(t, economy.storageKg) : []), [economy, t]);
  const sold = useMemo(() => (economy ? kgRows(t, economy.soldKg) : []), [economy, t]);
  const rejectionHint = (row: ScenarioEconomyRejected) => (
    REJECTION_CODES.has(row.code) ? t(`economyPanel.rejection.${row.code}`) : row.message
  );

  if (specErrors.length > 0) {
    return (
      <Card size="small" title={t('economyPanel.title')}>
        <Alert
          type="error"
          showIcon
          message={t('economyPanel.specUnreadable')}
          description={(
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {specErrors.map((error) => (
                <li key={`${error.code}:${error.path}`}>
                  <Text code>{error.path}</Text> {error.message}
                </li>
              ))}
            </ul>
          )}
        />
      </Card>
    );
  }

  if (!economy) {
    return (
      <Card size="small" title={t('economyPanel.title')}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('economyPanel.noEconomy')}
        />
      </Card>
    );
  }

  const net = economy.balance - economy.startingBalance;

  return (
    <Card
      size="small"
      title={(
        <Space wrap>
          {t('economyPanel.title')}
          <Tag color={economy.balance > 0 ? 'green' : 'default'}>
            {money(economy.balance, economy.currency)}
          </Tag>
          <Text type="secondary">
            {t('economyPanel.summary', {
              start: economy.startingBalance,
              revenue: economy.revenue,
              spent: economy.spent,
            })}
            {net !== 0 ? t('economyPanel.summaryNet', { net: `${net > 0 ? '+' : ''}${net}` }) : ''}
          </Text>
        </Space>
      )}
    >
      {economy.batteryExhausted && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('economyPanel.batteryExhausted.title', { date: economy.stoppedAtDate })}
          description={t('economyPanel.batteryExhausted.description')}
        />
      )}

      <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label={t('economyPanel.storage')}>
          {storage.length ? storage.join(', ') : <Text type="secondary">{t('economyPanel.storageEmpty')}</Text>}
        </Descriptions.Item>
        <Descriptions.Item label={t('economyPanel.sold')}>
          {sold.length ? sold.join(', ') : <Text type="secondary">{t('economyPanel.soldNothing')}</Text>}
        </Descriptions.Item>
        <Descriptions.Item label={t('economyPanel.prices')} span={2}>
          {Object.keys(economy.sellPrices).length
            ? Object.entries(economy.sellPrices).map(([crop, price]) => t('economyPanel.pricePerKg', { crop, price })).join(' · ')
            : <Text type="secondary">{t('economyPanel.nothingBought')}</Text>}
        </Descriptions.Item>
        {economy.battery && (
          <Descriptions.Item label={t('economyPanel.battery.label')} span={2}>
            <Space>
              <Progress
                percent={Math.round((economy.battery.charge / economy.battery.capacity) * 100)}
                size="small"
                style={{ width: 160 }}
                status={economy.batteryExhausted ? 'exception' : 'normal'}
              />
              <Text type="secondary">
                {t('economyPanel.battery.charge', {
                  charge: economy.battery.charge,
                  capacity: economy.battery.capacity,
                  cost: economy.battery.costPerDay,
                })}
                {economy.battery.regenPerDay > 0
                  ? t('economyPanel.battery.regen', { regen: economy.battery.regenPerDay })
                  : ''}
              </Text>
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>

      {economy.catalog.length > 0 && (
        <Table
          rowKey={(item) => item.id}
          size="small"
          pagination={false}
          style={{ marginBottom: 12 }}
          dataSource={economy.catalog}
          columns={[
            { title: t('economyPanel.catalog.item'), dataIndex: 'title' },
            { title: t('economyPanel.catalog.kind'), dataIndex: 'kind', width: 90, render: (kind: string) => <Tag>{kind}</Tag> },
            { title: t('economyPanel.catalog.price'), dataIndex: 'price', width: 80 },
            {
              title: t('economyPanel.catalog.stock'),
              dataIndex: 'stock',
              width: 110,
              render: (stock: number | null) => (stock == null ? '∞' : stock),
            },
            {
              title: t('economyPanel.catalog.boughtOwned'),
              key: 'owned',
              width: 150,
              render: (_, item) => `${item.bought} / ${item.owned}`,
            },
            {
              title: t('economyPanel.catalog.window'),
              dataIndex: 'maxWindowDays',
              width: 90,
              render: (days: number | null) => (days == null ? '—' : t('economyPanel.catalog.windowDays', { count: days })),
            },
          ]}
        />
      )}

      <Table<ScenarioEconomyApplied>
        rowKey={(row, index) => `${row.id || 'cmd'}-${index}`}
        size="small"
        pagination={false}
        title={() => <Text strong>{t('economyPanel.applied.title')}</Text>}
        locale={{ emptyText: t('economyPanel.applied.empty') }}
        dataSource={economy.applied}
        columns={[
          { title: t('economyPanel.columns.day'), dataIndex: 'date', width: 110 },
          { title: t('economyPanel.columns.what'), key: 'what', render: (_, row) => describeApplied(t, row) },
          {
            title: t('economyPanel.columns.amount'),
            dataIndex: 'amount',
            width: 90,
            render: (amount?: number) => (amount == null
              ? '—'
              : <Text type={amount >= 0 ? 'success' : 'danger'}>{amount > 0 ? `+${amount}` : amount}</Text>),
          },
          { title: t('economyPanel.columns.balance'), dataIndex: 'balanceAfter', width: 90 },
        ]}
      />

      {economy.rejected.length > 0 && (
        <Table<ScenarioEconomyRejected>
          rowKey={(row, index) => `${row.id || 'rej'}-${index}`}
          size="small"
          pagination={false}
          style={{ marginTop: 12 }}
          title={() => <Text strong type="danger">{t('economyPanel.rejected.title')}</Text>}
          dataSource={economy.rejected}
          columns={[
            { title: t('economyPanel.columns.day'), dataIndex: 'date', width: 110 },
            { title: t('economyPanel.columns.command'), dataIndex: 'type', width: 150 },
            {
              title: t('economyPanel.columns.reason'),
              key: 'why',
              render: (_, row) => (
                <Tooltip title={rejectionHint(row)}>
                  <Space direction="vertical" size={0}>
                    <Tag color="red">{row.code}</Tag>
                    <Text type="secondary">{rejectionHint(row)}</Text>
                  </Space>
                </Tooltip>
              ),
            },
          ]}
        />
      )}

      {economy.unscheduled.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={t('economyPanel.unscheduled', { count: economy.unscheduled.length })}
        />
      )}
    </Card>
  );
};

export default ScenarioEconomyPanel;
